import ProgressBar from '@ramonak/react-progress-bar';
import Image from 'next/image';
import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import type { SwiperOptions } from 'swiper';
import 'swiper/swiper-bundle.min.css';

import Button from '@components/atoms/button';
import { BLANK_SHIRT_PATH } from '@lib/utils';
import { useTeamKitImages } from '@hooks/useTeamKitImages';
import {
  BodyExtraSmallRegular,
  BodyRegularBold,
  BodySmallRegular
} from '@components/atoms/typography';
import { LeagueStatsEntry } from '@api-service/mini-leagues';
import { theme } from 'tailwind.config';

const MAX_VISIBLE_STATS = 5;

type MiniLeagueStatsCardProps = {
  header: string;
  subHeader: string;
  stats: LeagueStatsEntry[] | undefined;
};

const MiniLeagueStatsCard = ({
  header,
  subHeader,
  stats
}: MiniLeagueStatsCardProps) => {
  const [viewAll, setViewAll] = useState(false);
  // Hooks run before the early return so their order never varies.
  const { resolveTeamKit } = useTeamKitImages();
  if (!stats?.length) return null;

  const visibleStats = viewAll ? stats : stats.slice(0, MAX_VISIBLE_STATS);

  return (
    <div>
      <div className="mb-5">
        <div className="flex justify-between items-start mb-3">
          <BodyRegularBold>{header}</BodyRegularBold>
          {stats.length > MAX_VISIBLE_STATS && (
            <Button
              onClick={() => setViewAll(!viewAll)}
              variant="tertiary"
              className="!m-0"
            >
              {viewAll ? 'Collapse' : 'View all'}
            </Button>
          )}
        </div>
        <div className="flex flex-col">
          <BodyExtraSmallRegular>{subHeader}</BodyExtraSmallRegular>
        </div>
      </div>
      <div>
        {visibleStats.map((player, index) => {
          const percentage = Math.abs(player.percentage);
          return (
            <div
              key={`${player.displayName}-${index}`}
              className="flex items-center mt-3"
            >
              <div className="w-[60px] h-[60px] mr-2 relative">
                <Image
                  src={
                    resolveTeamKit(player.team.shortName) ?? BLANK_SHIRT_PATH
                  }
                  alt={`${player.team.shortName} shirt`}
                  fill
                  sizes="60px"
                  style={{ objectFit: 'contain' }}
                  unoptimized
                />
              </div>
              <div className="flex flex-col flex-1">
                <div className="flex justify-between items-center">
                  <BodySmallRegular>{player.displayName}</BodySmallRegular>
                  <BodySmallRegular>{percentage.toFixed(1)}%</BodySmallRegular>
                </div>
                <ProgressBar
                  bgColor={theme.colors.primary[600]}
                  baseBgColor={theme.colors.neutral[300]}
                  height="3px"
                  completed={percentage}
                  width="95%"
                  isLabelVisible={false}
                  className="my-3"
                  animateOnRender
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

type MiniLeagueStatsProps = {
  captainStats?: LeagueStatsEntry[];
  ownershipStats?: LeagueStatsEntry[];
  influenceStats?: LeagueStatsEntry[];
  threatStats?: LeagueStatsEntry[];
  breakpoints?: { [width: number]: SwiperOptions };
  captainSubHeader: string;
  influenceSubHeader: string;
  threatSubHeader: string;
  ownershipSubHeader: string;
};

const MiniLeagueStats = ({
  captainStats,
  ownershipStats,
  influenceStats,
  threatStats,
  breakpoints,
  captainSubHeader,
  influenceSubHeader,
  threatSubHeader,
  ownershipSubHeader
}: MiniLeagueStatsProps) => {
  return (
    <Swiper
      modules={[Pagination]}
      pagination={{ clickable: true, el: '.custom-pagination' }}
      spaceBetween={16}
      slidesPerView={1.1}
      centeredSlides={true}
      centeredSlidesBounds={true}
      breakpoints={breakpoints}
      className="my-2"
      style={{ padding: '16px 8px' }}
    >
      {Boolean(captainStats?.length) && (
        <SwiperSlide className="shadow-300 rounded-lg p-5">
          <MiniLeagueStatsCard
            header={`👨‍✈️ Captain`}
            subHeader={captainSubHeader}
            stats={captainStats}
          />
        </SwiperSlide>
      )}
      <SwiperSlide className="shadow-300 rounded-lg p-5">
        <MiniLeagueStatsCard
          header={`🌟 Influence`}
          subHeader={influenceSubHeader}
          stats={influenceStats}
        />
      </SwiperSlide>
      <SwiperSlide className="shadow-300 rounded-lg p-5">
        <MiniLeagueStatsCard
          header={`❗ Threat`}
          subHeader={threatSubHeader}
          stats={threatStats}
        />
      </SwiperSlide>
      {Boolean(ownershipStats?.length) && (
        <SwiperSlide className="shadow-300 rounded-lg p-5">
          <MiniLeagueStatsCard
            header={`💼 Ownership`}
            subHeader={ownershipSubHeader}
            stats={ownershipStats}
          />
        </SwiperSlide>
      )}
      <div className="block md:hidden pt-6">
        <div className="custom-pagination"></div>
      </div>
    </Swiper>
  );
};

export default MiniLeagueStats;
